import React, { useMemo } from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Text } from './AppText';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ThemeColors } from '../theme';
import { useTheme, useThemeMode } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';
import { useUnread } from '../unread/UnreadContext';
import { formatBadgeCount } from '../unread/badge';
import { floatingDockMetrics } from './floatingDockLayout';
import { getGlassView } from './nativeGlass';

type Tab = 'discover' | 'inbox' | 'calls' | 'calendar' | 'profile';

type Props = {
  active: Tab;
  onPress: (tab: Tab) => void;
};

export function dockIconColor(active: boolean, isDark: boolean): string {
  return active ? (isDark ? '#A9C7FF' : '#17365D') : (isDark ? '#FFFFFF' : '#000000');
}

function useDockIconColor(active: boolean) {
  const { isDark } = useThemeMode();
  return dockIconColor(active, isDark);
}

function DiscoverIcon({ active }: { active: boolean }) {
  const c = useDockIconColor(active);
  return <Ionicons name="grid" size={25} color={c} />;
}

function InboxIcon({ active }: { active: boolean }) {
  const c = useDockIconColor(active);
  return <Ionicons name="chatbubble" size={26} color={c} />;
}

// Aramalar (9.4.10). Klasik telefon ahizesi — evrensel "çağrı" simgesi ve
// uygulamanın kendi çağrı ikonuyla (CallScreen'deki Ionicons 'call') aynı.
// Ahize eğrisi View'la temiz çıkmadığından ikon-fontu kullanılıyor; dock'un
// rozet metni ayrı testID ile hedeflendiği için "metin yok" değişmezi rozete
// özgü kalıyor.
function CallsIcon({ active }: { active: boolean }) {
  const c = useDockIconColor(active);
  return <Ionicons name="call" size={26} color={c} />;
}

export function CalendarIcon({ active, color }: { active: boolean; color?: string }) {
  const colors = useTheme();
  const c = color ?? (active ? colors.black : colors.gray400);
  return (
    <View style={icon.calendarWrap}>
      <View style={icon.calendarRings}>
        <View style={[icon.calendarRing, { backgroundColor: c }]} />
        <View style={[icon.calendarRing, { backgroundColor: c }]} />
      </View>
      <View style={[icon.calendarBody, { borderColor: c }]}>
        <View style={[icon.calendarBand, { backgroundColor: c }]} />
      </View>
    </View>
  );
}

function ProfileIcon({ active }: { active: boolean }) {
  const c = useDockIconColor(active);
  return <Ionicons name="person" size={25} color={c} />;
}

// Rozet (9.26.7.2): sayı — sohbet satırındaki rozetle tutarlı olsun diye — ve
// iki haneyi geçince "99+". Sayı yalnız görsel kalmasın diye erişilebilirlik
// etiketi sekmenin kendisinde: ekran okuyucu "Mesajlar, 3 okunmamış" der.
function Badge({ count }: { count: number }) {
  const colors = useTheme();
  const { lang } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  if (count <= 0) return null;
  return (
    <View style={styles.badge} pointerEvents="none" accessibilityElementsHidden importantForAccessibility="no-hide-descendants">
      <Text testID="dock-badge" style={styles.badgeText} numberOfLines={1}>{formatBadgeCount(count, lang)}</Text>
    </View>
  );
}

export default function FloatingDock({ active, onPress }: Props) {
  const insets = useSafeAreaInsets();
  const colors = useTheme();
  const { isDark } = useThemeMode();
  const { t } = useTranslation();
  const { messages, meetups } = useUnread();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  const label = (key: string, count: number, countKey: string) =>
    count > 0 ? `${t(key)}, ${t(countKey, { count })}` : t(key);

  /**
   * 7.14 · D-548 — alt çubuk, camın kendini GERÇEKTEN gösterdiği ilk yüzey.
   * 7.14 · D-700 — beş sekme birbirinden ayrı: her sembolün kendi yuvarlağı
   * var, aralarında boşluk, ortak bir çubuk yok. Yazı da yok; yalnız semboller.
   * Her yuvarlak kendi zeminini taşıyor, çünkü listeler altından kayıyor:
   * zeminsiz bir sembol altından geçen yazının üstüne biner (D-549'daki ders).
   */
  const GlassView = useMemo(() => getGlassView(), []);

  const tab = (
    key: Tab,
    accessibilityLabel: string,
    symbol: React.ReactNode,
    badge?: number,
  ) => (
    <TouchableOpacity
      key={key}
      testID={`dock-tab-${key}`}
      style={[styles.tab, !GlassView && styles.tabSurface]}
      onPress={() => onPress(key)}
      accessibilityRole="button"
      accessibilityState={{ selected: active === key }}
      accessibilityLabel={accessibilityLabel}>
      {GlassView && (
        /* Malzeme kendi dolgusunu ve gölgesini taşır; dokunmayı engellemesin. */
        <GlassView
          style={styles.tabGlass}
          glassEffectStyle="regular"
          isInteractive
          colorScheme={isDark ? 'dark' : 'light'}
          pointerEvents="none"
        />
      )}
      <View testID={`dock-symbol-${key}`} pointerEvents="none" style={styles.symbol}>
        {symbol}
      </View>
      {badge !== undefined && <Badge count={badge} />}
    </TouchableOpacity>
  );

  return (
    <View
      testID="floating-dock"
      style={[styles.dock, { bottom: insets.bottom + floatingDockMetrics.bottomOffset }]}>
      {tab('discover', t('dock.discover'), <DiscoverIcon active={active === 'discover'} />)}
      {tab('inbox', label('dock.inbox', messages, 'dock.unreadMessages'), <InboxIcon active={active === 'inbox'} />, messages)}
      {tab('calls', t('dock.calls'), <CallsIcon active={active === 'calls'} />)}
      {tab('calendar', label('dock.calendar', meetups, 'dock.pendingRequests'), <Ionicons name="calendar" size={26} color={dockIconColor(active === 'calendar', isDark)} />, meetups)}
      {tab('profile', t('dock.profile'), <ProfileIcon active={active === 'profile'} />)}
    </View>
  );
}

const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  // Render scalable font outlines at final size, without fractional View scaling.
  symbol: { width: 28, height: 28, alignItems: 'center', justifyContent: 'center' },
  /** Yalnız yerleşim: beş yuvarlak yan yana, aralarında boşluk; ortak zemin yok. */
  dock: {
    position: 'absolute',
    alignSelf: 'center',
    flexDirection: 'row',
    paddingVertical: floatingDockMetrics.verticalPadding,
    gap: floatingDockMetrics.tabGap,
  },
  tab: {
    alignItems: 'center',
    justifyContent: 'center',
    width: floatingDockMetrics.tabSize,
    height: floatingDockMetrics.tabSize,
    borderRadius: floatingDockMetrics.tabSize / 2,
  },
  /** Cam yokken (Android, eski iOS, web) her yuvarlağın kendi beyaz dolgusu ve gölgesi. */
  tabSurface: {
    backgroundColor: colors.white,
    shadowColor: colors.black,
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.12,
    shadowRadius: 10,
    elevation: 6,
  },
  tabGlass: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: floatingDockMetrics.tabSize / 2,
  },
  // İkonun sağ üstüne oturur; dokunma hedefini bozmasın diye akıştan çıkarıldı
  // (position: 'absolute' + pointerEvents="none").
  badge: {
    position: 'absolute',
    top: floatingDockMetrics.badgeInset,
    right: floatingDockMetrics.badgeInset,
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    paddingHorizontal: 4,
    backgroundColor: '#e53935',
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeText: {
    color: '#fff',
    fontSize: 10,
    lineHeight: 13,
    fontWeight: '700',
  },
});

const icon = StyleSheet.create({
  grid: {
    width: 18,
    height: 18,
    flexWrap: 'wrap',
    flexDirection: 'row',
    gap: 4,
  },
  dot: {
    width: 7,
    height: 7,
    borderRadius: 2,
  },
  bubble: {
    width: 20,
    height: 17,
    borderRadius: 6,
    borderWidth: 2,
    justifyContent: 'flex-end',
  },
  bubbleTail: {
    width: 5,
    height: 4,
    borderRadius: 1,
    marginLeft: 3,
    marginBottom: -3,
  },
  person: { alignItems: 'center', gap: 2 },
  head: { width: 10, height: 10, borderRadius: 5 },
  body: { width: 16, height: 8, borderRadius: 8 },
  calendarWrap: { alignItems: 'center' },
  calendarRings: { flexDirection: 'row', gap: 5, marginBottom: -2, zIndex: 1 },
  calendarRing: { width: 2, height: 5, borderRadius: 1 },
  calendarBody: { width: 18, height: 16, borderRadius: 4, borderWidth: 1.5, overflow: 'hidden' },
  calendarBand: { height: 2, width: '100%', marginTop: 3 },
});
